import React, { useState, useEffect } from 'react';
import {
    BarChart3, TrendingUp, Users, ShoppingCart, DollarSign, Package,
    AlertTriangle, PieChart, Download, RefreshCw, Loader2,
    ArrowUp, ArrowDown, Eye, CreditCard, Star, Shield
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { adminApi } from '@/services/api/admin';
import { formatPrice } from '@/utils/formatters';

interface AnalyticsData {
    sales_by_category: {
        name: string;
        total_revenue: string;
    }[];
    customer_ltv: number;
    low_stock_products: {
        id: number;
        name: string;
        stock_quantity: number;
    }[];
    monthly_growth: {
        month: string;
        total_revenue: string;
        mom_growth_percentage: number | null;
    }[];
    // Datos adicionales sugeridos
    total_revenue?: number;
    total_orders?: number;
    total_customers?: number;
    conversion_rate?: number;
    top_products?: {
        id: number;
        name: string;
        units_sold: number;
        revenue: number;
    }[];
    customer_acquisition?: {
        channel: string;
        customers: number;
        cost: number;
    }[];
}

interface AdminAnalyticsProps {
    onNavigateBack?: () => void;
}

const AdminAnalyticsComponent: React.FC<AdminAnalyticsProps> = ({
    onNavigateBack
}) => {
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'ytd'>('30d');
    const [refreshing, setRefreshing] = useState(false);

    const fetchAnalytics = async () => {
        setIsLoading(true);
        try {
            const response = await adminApi.getAnalytics({ period: timeRange });
            //console.log('Analytics data:', response);
            setAnalyticsData(response);
        } catch (error: any) {
            console.error('Error loading analytics:', error);
            toast.error('Error al cargar los análisis');
        } finally {
            setIsLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [timeRange]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchAnalytics();
    };

    const formatMonth = (monthString: string) => {
        const [year, month] = monthString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    };

    const getGrowthIcon = (percentage: number | null) => {
        if (percentage === null || percentage === 0) return null;
        return percentage > 0 ? (
            <ArrowUp className="w-4 h-4 text-green-500" />
        ) : (
            <ArrowDown className="w-4 h-4 text-red-500" />
        );
    };

    const getGrowthColor = (percentage: number | null) => {
        if (percentage === null || percentage === 0) return 'text-gray-500';
        return percentage > 0 ? 'text-green-500' : 'text-red-500';
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando análisis...</p>
                </div>
            </div>
        );
    }

    if (!analyticsData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No se pudieron cargar los datos de análisis</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                                <BarChart3 className="w-6 h-6 mr-2" />
                                Dashboard Analítico
                            </h1>
                            <p className="mt-1 text-gray-600">
                                Métricas y análisis del performance del marketplace
                            </p>
                        </div>
                        <div className="mt-4 sm:mt-0 flex space-x-3">
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value as any)}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="7d">Últimos 7 días</option>
                                <option value="30d">Últimos 30 días</option>
                                <option value="90d">Últimos 90 días</option>
                                <option value="ytd">Este año</option>
                            </select>
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                {refreshing ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                )}
                                Actualizar
                            </button>
                            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                                <Download className="w-4 h-4 mr-2" />
                                Exportar
                            </button>
                            {onNavigateBack && (
                                <button
                                    onClick={onNavigateBack}
                                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                                >
                                    Volver al Dashboard
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <DollarSign className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Ingresos Totales</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {formatPrice(analyticsData.total_revenue || 0)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Período seleccionado</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <ShoppingCart className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Órdenes</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {analyticsData.total_orders?.toLocaleString() || '0'}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Pedidos completados</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Users className="w-6 h-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Clientes Totales</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {analyticsData.total_customers?.toLocaleString() || '0'}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Usuarios registrados</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <CreditCard className="w-6 h-6 text-orange-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">LTV Cliente</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {formatPrice(analyticsData.customer_ltv)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Promedio de Compra</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Ventas por Categoría */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <PieChart className="w-5 h-5 mr-2" />
                            Ventas por Categoría
                        </h3>
                        <div className="space-y-4">
                            {analyticsData.sales_by_category.map((category, index) => (
                                <div key={index} className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{category.name}</span>
                                    <span className="text-sm font-bold text-gray-900">
                                        {formatPrice(parseFloat(category.total_revenue))}
                                    </span>
                                </div>
                            ))}
                            {analyticsData.sales_by_category.length === 0 && (
                                <p className="text-gray-500 text-sm">No hay datos de ventas por categoría</p>
                            )}
                        </div>
                    </div>

                    {/* Crecimiento Mensual */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <TrendingUp className="w-5 h-5 mr-2" />
                            Crecimiento Mensual
                        </h3>
                        <div className="space-y-3">
                            {analyticsData.monthly_growth.map((month, index) => (
                                <div key={index} className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">{formatMonth(month.month)}</span>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm font-bold text-gray-900">
                                            {formatPrice(parseFloat(month.total_revenue))}
                                        </span>
                                        {month.mom_growth_percentage !== null && (
                                            <span className={`text-xs flex items-center ${getGrowthColor(month.mom_growth_percentage)}`}>
                                                {getGrowthIcon(month.mom_growth_percentage)}
                                                {Math.abs(month.mom_growth_percentage)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {analyticsData.monthly_growth.length === 0 && (
                                <p className="text-gray-500 text-sm">No hay datos de crecimiento mensual</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Productos con Stock Bajo */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <AlertTriangle className="w-5 h-5 mr-2 text-yellow-600" />
                        Productos con Stock Bajo
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Producto
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Stock Actual
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {analyticsData.low_stock_products.map((product) => (
                                    <tr key={product.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                            <div className="text-sm text-gray-500">ID: #{product.id}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${product.stock_quantity === 0
                                                ? 'bg-red-100 text-red-800'
                                                : product.stock_quantity <= 5
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-green-100 text-green-800'
                                                }`}>
                                                {product.stock_quantity} unidades
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-600">
                                                {product.stock_quantity === 0 ? 'Agotado' : 'Stock bajo'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                                                <Eye className="w-4 h-4 inline mr-1" />
                                                Ver producto
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {analyticsData.low_stock_products.length === 0 && (
                            <div className="text-center py-8">
                                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">Todos los productos tienen stock suficiente</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Secciones adicionales para datos sugeridos */}
                {analyticsData.top_products && analyticsData.top_products.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Star className="w-5 h-5 mr-2 text-yellow-500" />
                            Productos Más Vendidos
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {analyticsData.top_products.slice(0, 6).map((product, index) => (
                                <div key={product.id} className="border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-900">
                                            #{index + 1} {product.name}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {product.units_sold} unidades vendidas
                                    </div>
                                    <div className="text-sm font-bold text-gray-900">
                                        {formatPrice(product.revenue)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {analyticsData.customer_acquisition && analyticsData.customer_acquisition.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Shield className="w-5 h-5 mr-2 text-blue-500" />
                            Adquisición de Clientes
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {analyticsData.customer_acquisition.map((channel, index) => (
                                <div key={index} className="border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-900">{channel.channel}</span>
                                        <span className="text-sm text-gray-600">{channel.customers} clientes</span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-gray-600">Costo: </span>
                                        <span className="font-bold text-gray-900">{formatPrice(channel.cost)}</span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-gray-600">CAC: </span>
                                        <span className="font-bold text-gray-900">
                                            {formatPrice(channel.cost / (channel.customers || 1))}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminAnalyticsComponent;